import { SLUG_VALID_REGEX } from 'views/Content/ContentForm/constants';
import { z } from 'zod';

import {
  EBlockType,
  EContentType,
  EPagesRadioButtonOptions
} from './constants';

export const imageItemSchema = z.object({
  type: z.literal(EBlockType.IMAGE),
  url: z.string().min(1, { message: 'This field is required.' }),
  mobileUrl: z.string().optional().nullable(),
  redirectUrl: z.string().optional().nullable()
});

export const videoItemSchema = z.object({
  type: z.literal(EBlockType.VIDEO),
  url: z
    .string()
    .min(1, { message: 'This field is required.' })
    .url({ message: 'Please enter a valid URL.' }),
  mobileUrl: z.string().optional().nullable(),
  autoplay: z.boolean().default(false)
});

export const ctaItemSchema = z.object({
  type: z.literal(EBlockType.CTA),
  text: z.string().optional(),
  redirectUrl: z
    .string()
    .min(1, { message: 'This field is required.' })
    .url({ message: 'Please enter a valid URL.' }),
  assetUrl: z.string().optional().nullable(),
  assetUrlMobile: z.string().optional().nullable(),
  isStoreRedirect: z.boolean().optional()
});

const blockItemSchema = z.discriminatedUnion('type', [
  imageItemSchema,
  videoItemSchema,
  ctaItemSchema
]);

export const regularBlockSchema = z.object({
  type: z.union([
    z.literal(EBlockType.IMAGE),
    z.literal(EBlockType.VIDEO),
    z.literal(EBlockType.CTA)
  ]),
  itemsPerRow: z.number().default(2),
  items: z.array(blockItemSchema).optional()
});

export const contentsPreviewBlockSchema = z.object({
  type: z.literal(EBlockType.CONTENTS_PREVIEW),
  contentType: z.nativeEnum(EContentType),
  itemsPerRow: z.number().default(2),
  title: z.string().nonempty({ message: 'This field is required.' }),
  maxItems: z.number().min(1, { message: 'Minimum 1 item is required' })
});

export const htmlBlockSchema = z.object({
  type: z.literal(EBlockType.HTML),
  itemsPerRow: z.number().default(2),
  html: z.string().min(1, { message: 'This field is required.' })
});

export const dailyBonusBlockSchema = z.object({
  type: z.literal(EBlockType.DAILY_BONUS),
  itemsPerRow: z.number().default(2),
  backgroundImage: z.string().optional().nullable(),
  backgroundImageMobile: z.string().optional().nullable(),
  redirectUrl: z.string().optional().nullable(),
  isStoreRedirect: z.boolean().optional()
});

export const progressBarBlockSchema = z.object({
  type: z.literal(EBlockType.PROGRESS_BAR),
  itemsPerRow: z.number().default(2),
  backgroundImage: z.string().optional().nullable(),
  backgroundImageMobile: z.string().optional().nullable(),
  isStoreRedirect: z.boolean().optional(),
  redirectUrl: z.string().optional().nullable()
});

// Union schema to ensure blocks are one of: regular, contents preview, or daily bonus (mutually exclusive)
export const blockV2Schema = z.union([
  regularBlockSchema,
  contentsPreviewBlockSchema,
  htmlBlockSchema,
  dailyBonusBlockSchema,
  progressBarBlockSchema
]);

export const blocksV2ArraySchema = z.array(blockV2Schema);
export const blocksV2Schema = blocksV2ArraySchema.optional();

export const pageFormSchema = z
  .object({
    pageName: z.string().min(1, { message: 'This field is required.' }),
    pageIcon: z.string().min(1, { message: 'This field is required.' }),
    slug: z
      .string()
      .optional()
      .refine((val) => !val || SLUG_VALID_REGEX.test(val), {
        message:
          'This field can only contain letters, numbers, and hyphens. Cannot end with a hyphen.'
      }),
    pageTemplate: z.string().min(1, { message: 'This field is required.' }),
    visibility: z.string().min(1, { message: 'This field is required.' }),
    priority: z.coerce.number().nullish(),
    subPagePriority: z.coerce.number().nullish(),
    metadataImage: z.string().optional(),
    metadataDescription: z.string().optional(),
    state: z.nativeEnum(EPagesRadioButtonOptions),
    publishDate: z.string().optional(),
    isPublishDateEnabled: z.boolean().optional(),
    isSubPage: z.boolean(),
    parentPageId: z.string().nullish(),
    blocksV2: blocksV2Schema
  })
  .refine(
    (data) => {
      if (data.isSubPage) {
        return data.parentPageId !== null && data.parentPageId !== undefined;
      }
      return true;
    },
    {
      message: 'Parent page is required for sub pages',
      path: ['parentPageId']
    }
  );
